import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Text, {
  TEXT_COLOR,
  TEXT_SIZE,
  TEXT_KIND,
} from '@/bit/components/components.text';
import { Calendar } from '@/bit/components/components.calendar';
import Icon from "@/bit/components/components.icon-glyph";
import CustomLink from "@/bit/components/components.custom-link";
import { sendTaggingInfo, getToday, calendarLocaleES  } from './utils';

const parseToCalendarValue = (dateString) => {
  if (!dateString) return undefined;
  const parts = dateString.split('/');
  if (parts.length !== 3) return undefined;
  const [day, month, year] = parts.map(Number);
  if (!day || !month || !year || year < 1000) return undefined;
  return { day, month, year };
};

function DatePicker({
  className,
  id,
  name,
  label,
  error,
  onChange,
  value,
  disabled,
  sendTagging,
  validation
}) {
  const [selectedDate, setSelectedDate] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const isBefore = validation == 'dateBefore' ?  true : validation === 'dateAfter' ? false : null;


  const taggingInfo = {
    componentVariation: 'all',
    componentContent: label,
    componentProduct: '',
    componentName: 'date',
  };

  const handleCalendarChange = (newDate) => {
    const { day, month, year } = newDate;
    const formattedDate = `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
    if (sendTagging) {
      sendTaggingInfo(taggingInfo, 'change');
    }
    setSelectedDate(formattedDate);
    setIsOpen(false);
    onChange({ value: formattedDate, name: name });
  };

  const handleInputChange = (event) => {
    setSelectedDate(event.target.value);
  };

  const handleInputBlur = () => {
    if (sendTagging) {
      sendTaggingInfo(taggingInfo, 'change');
    }
    onChange({ value: selectedDate, name: name });
  };

  const handleWrapperClick = () => {
    if (!disabled) setIsOpen((prev) => !prev);
  };

  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    setSelectedDate(value);
  }, [value]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      className={`datepicker-wrapper ${className}`}
      data-cy={id}
      ref={wrapperRef}
    >
      <CustomLink
            type="button"
            className="datepicker-wrapper_date_icon"
            data-cy={`${id}__icon`}
            disabled={disabled}
            taggingInfo={{ ...taggingInfo, componentContent: 'select' }}
             onClick={handleWrapperClick}
      >
      <div
        className={`datepicker-wrapper_date ${error ? 'error' : ''} ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
        data-cy={`${id}__input-wrapper`}
      >
        {label && (
          <Text
            className="datepicker-wrapper_date_label"
            text={label}
            color={error ? TEXT_COLOR.RED : TEXT_COLOR.MOVISTAR_BLUE}
            kind={TEXT_KIND.SPAN}
            size={TEXT_SIZE.TEXT_PRESET_01}
            cyData={`${id}__label`}
          />
        )}
        <div className="datepicker-wrapper_input">
          <input
            id={name}
            name={name}
            type="text"
            value={selectedDate}
            placeholder="dd/mm/aaaa"
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            disabled={disabled}
            data-cy={`${id}__input`}
          />
          <Icon id="calendario-mes-m" height={30} color={TEXT_COLOR.MOVISTAR_BLUE} />
        </div>
      </div>
      </CustomLink>
      {isOpen && (
        <div className="datepicker-wrapper_calendar" data-cy={`${id}__calendar`}>
          <Calendar
            value={parseToCalendarValue(selectedDate)}
            onChange={handleCalendarChange}
            locale={calendarLocaleES}
            colorPrimary="#0066FF"
            maximumDate={isBefore == true ? getToday() : null}
            minimumDate={isBefore == false ? getToday() : null}
          />
        </div>
      )}
      {error && (
        <Text
          className="datepicker-wrapper_error"
          text={error}
          color={TEXT_COLOR.RED}
          kind={TEXT_KIND.SPAN}
          size={TEXT_SIZE.CAPTION3}
          cyData={`${id}__error`}
        />
      )}
      <style jsx>{`.datepicker-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
}
.datepicker-wrapper_date {
    display: block;
    flex-direction: column;
    border: 0.0625rem solid #cccccc;
    border-radius: 0.25rem;
    padding: 0.375rem 0.75rem;
    background-color: #ffffff;
    min-width: 14rem;
    position: relative;
    cursor: pointer;
}
.datepicker-wrapper_input {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.datepicker-wrapper_date.open {
    border-color: #0066FF;
}
.datepicker-wrapper_date.error {
    border-color: #e53935;
}
.datepicker-wrapper_date.disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
}
.datepicker-wrapper_date input {
    border: none;
    outline: none;
    font-size: 1rem;
    color: #333333;
    background: transparent;
    cursor: pointer;
    padding: 0;
    width: 100%;
}
.datepicker-wrapper_date input:disabled {
    cursor: not-allowed;
    color: #cccccc;
}
.datepicker-wrapper_date_icon {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    color: #333333;
}
.datepicker-wrapper_date_icon:disabled {
    cursor: not-allowed;
    color: #cccccc;
}
.datepicker-wrapper_calendar {
    position: absolute;
    top: calc(100% + 0.25rem);
    left: 0;
    z-index: 100;
    background-color: #ffffff;
    border: 0.0625rem solid #cccccc;
    border-radius: 0.25rem;
    box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.1);
}
:global(.datepicker-wrapper_error) {
    padding: 0.125rem 0 0 0;
}
:global(.datepicker-wrapper_date_label) {
    margin-bottom: 0.125rem;
}`}</style>
    </div>
  );
}

DatePicker.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.string,
  error: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  sendTagging: PropTypes.bool,
};

DatePicker.defaultProps = {
  className: '',
  label: '',
  value: '',
  error: '',
  onChange: () => {},
  disabled: false,
  sendTagging: false,
};

export default DatePicker;